import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Check, ArrowLeft, CreditCard, AlertCircle } from 'lucide-react';
import { getToken } from '../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubscribePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activity, plan } = location.state || {};
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('stripe');

  if (!activity || !plan) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-12 text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Erreur</h1>
          <p className="text-gray-600 mb-6">Aucun plan sélectionné.</p>
          <Button onClick={() => navigate('/communityplace')}>
            Retour aux offres
          </Button>
        </div>
      </div>
    );
  }

  const handleSubscribe = async () => {
    setLoading(true);
    setError('');

    try {
      const token = getToken();
      const response = await axios.post(
        `${BACKEND_URL}/api/subscriptions/subscribe`,
        {
          plan_id: plan.plan_id,
          activity_id: activity.id,
          payment_method: paymentMethod
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.success) {
        // TODO: Rediriger vers la page de paiement Stripe
        // Pour l'instant, on redirige vers une page de succès temporaire
        navigate('/subscription-success', { 
          state: { 
            subscription_id: response.data.subscription_id,
            activity: activity.name,
            plan: plan.name
          } 
        });
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Une erreur est survenue lors de la souscription');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      <Navigation />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Button
          onClick={() => navigate(-1)}
          variant="ghost"
          className="mb-6"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Retour
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Plan Summary */}
          <div className="lg:col-span-2">
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-blue-500 to-indigo-500 text-white">
                <CardTitle className="text-2xl">Confirmation de souscription</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Activité sélectionnée</h3>
                  <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                    <span className="text-3xl">{activity.icon}</span>
                    <div>
                      <p className="font-semibold text-gray-900">{activity.name}</p>
                      <p className="text-sm text-gray-600">{activity.description}</p>
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Plan choisi</h3>
                  <div className="p-4 bg-blue-50 rounded-lg border-2 border-blue-200">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-bold text-gray-900">{plan.name}</h4>
                      <div className="text-right">
                        <p className="text-2xl font-bold text-blue-600">{plan.price}€</p>
                        <p className="text-sm text-gray-600">
                          / {plan.billing_period === 'monthly' ? 'mois' : 'an'}
                        </p>
                      </div>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">{plan.description}</p>
                    
                    <div className="space-y-2">
                      <p className="text-sm font-semibold text-gray-700">Inclus dans ce plan :</p>
                      {plan.features && plan.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="h-4 w-4 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-sm text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Méthode de paiement</h3>
                  <div className="space-y-3">
                    <label className="flex items-center p-4 border-2 border-gray-200 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors">
                      <input
                        type="radio"
                        name="payment"
                        value="stripe"
                        checked={paymentMethod === 'stripe'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                        className="mr-3 h-4 w-4"
                      />
                      <CreditCard className="h-5 w-5 text-blue-600 mr-3" />
                      <div>
                        <p className="font-semibold text-gray-900">Carte bancaire</p>
                        <p className="text-sm text-gray-600">Paiement sécurisé via Stripe</p>
                      </div>
                    </label>
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <Button
                  onClick={handleSubscribe}
                  disabled={loading}
                  className="w-full bg-blue-600 hover:bg-blue-700 font-semibold py-4 text-lg"
                >
                  {loading ? 'Traitement en cours...' : `Confirmer et payer ${plan.price}€`}
                </Button>
              </CardContent>
            </Card>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <Card className="shadow-lg sticky top-6">
              <CardHeader className="bg-gray-50">
                <CardTitle>Résumé</CardTitle>
              </CardHeader>
              <CardContent className="p-6">
                <div className="space-y-3">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Plan</span>
                    <span className="font-semibold text-gray-900">{plan.name}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Période</span>
                    <span className="font-semibold text-gray-900">
                      {plan.billing_period === 'monthly' ? 'Mensuel' : 'Annuel'}
                    </span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Renouvellement</span>
                    <span className="font-semibold text-gray-900">Automatique</span>
                  </div>
                  
                  <div className="border-t pt-3">
                    <div className="flex justify-between items-center">
                      <span className="text-lg font-bold text-gray-900">Total</span>
                      <span className="text-2xl font-bold text-blue-600">{plan.price}€</span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      Facturé {plan.billing_period === 'monthly' ? 'mensuellement' : 'annuellement'}
                    </p>
                  </div>
                </div>

                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <p className="text-xs text-blue-900">
                    ✓ Accès immédiat après paiement<br />
                    ✓ Annulation possible à tout moment<br />
                    ✓ Paiement 100% sécurisé
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default SubscribePage;
